import * as p from '@clack/prompts'
import type {
  RailwayProjectContext,
  RailwayRunner,
  RailwayWorkspace
} from '@cli-types/next-adapter/railway.js'
import { spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { createRailwayProject } from './create-railway-project.js'
import { linkRailwayProject } from './link-railway-project.js'
import { listRailwayProjects } from './list-railway-projects.js'
import { readRailwayProjectContext } from './read-railway-project-context.js'

export async function ensureRailwayProject(
  runner: RailwayRunner,
  options: {
    cwd: string
    projectName: string
    interactive: boolean
    project?: string
    workspace?: string
    workspaces?: RailwayWorkspace[]
    environment?: string
    env?: NodeJS.ProcessEnv
  }
): Promise<RailwayProjectContext> {
  const environment = options.environment?.trim() || 'production'
  const projectSpinner = spinner()
  projectSpinner.start('Checking Railway project')

  try {
    if (options.project) {
      await linkRailwayProject(runner, options.cwd, options.project, environment, {
        workspace: options.workspace,
        env: options.env
      })
    } else {
      let createWorkspace = options.workspace
      if (!createWorkspace && options.workspaces?.length === 1) {
        createWorkspace = options.workspaces[0]?.id
      } else if (!createWorkspace && options.interactive && options.workspaces?.length) {
        projectSpinner.clear()
        const workspace = await p.select({
          message: 'Choose a Railway workspace',
          options: options.workspaces.map((candidate) => ({
            value: candidate.id,
            label: candidate.name
          }))
        })
        if (p.isCancel(workspace)) {
          p.cancel('Setup cancelled.')
          process.exit(0)
        }
        createWorkspace = workspace
        projectSpinner.start('Checking Railway project names')
      }

      const projects = await listRailwayProjects(runner, options.cwd, options.env)
      projectSpinner.message(`Creating Railway project ${pc.cyan(options.projectName)}`)
      const created = await createRailwayProject(
        runner,
        options.cwd,
        options.projectName,
        projects,
        {
          workspace: createWorkspace,
          env: options.env
        }
      )

      await linkRailwayProject(runner, options.cwd, created.id, environment, {
        workspace: created.workspace?.id ?? createWorkspace,
        env: options.env
      })

      const context = await readRailwayProjectContext(runner, options.cwd, environment, options.env)
      if (!context || context.id !== created.id) {
        throw new Error('Railway did not link the newly created project.')
      }
      projectSpinner.clear()
      return context
    }

    const context = await readRailwayProjectContext(runner, options.cwd, environment, options.env)
    projectSpinner.clear()
    if (!context) {
      throw new Error(`Railway project is linked, but environment "${environment}" is unavailable.`)
    }
    return context
  } finally {
    projectSpinner.clear()
  }
}
